import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AIAssistantService } from 'app/services/AIAssistant/ai-assistant.service';
import { UserService } from 'app/services/user.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { firstValueFrom } from 'rxjs';
import { SanitizationService } from 'app/shared/utils/sanitization.service';
import { FEATURE_FLAGS } from 'app/shared/constants/feature-flags.constants';

interface Message {
	role: 'user' | 'assistant';
	content: string;
	displayContent?: string;
	safeContent?: string;
	safeDisplayContent?: string;
	typing?: boolean;
	timestamp?: Date;
}

@Component({
	selector: 'app-ai-assistant',
	templateUrl: './ai-assistant.component.html',
	styleUrls: ['./ai-assistant.component.css'],
	imports: [FormsModule, CommonModule, RouterModule],
})
export class AiAssistantComponent implements OnInit {
	@ViewChild('chatScroll') private chatScrollRef!: ElementRef;
	messages: Message[] = [];
	userInput = '';
	charLimit = 1000;
	isLoading = false;
	threadId: string | null = null;
	userName = '';
	aiAgentsEnabled = false;
	// EN-4448: gates the "ask Claire for data/actions" pointer on the same client-scoped PortalAgent
	// flag that shows Claire's nav entry, so clients without Claire never see a dead reference.
	claireEnabled = false;

	private typingSound = new Audio('assets/sounds/typing.mp3');
	private readonly clientId: string;

	constructor(
		private http: HttpClient,
		private aiAssistantService: AIAssistantService,
		private userService: UserService,
		private readonly featureFlagService: FeatureFlagService,
		private readonly sanitizationService: SanitizationService
	) {
		this.userName = this.userService.currentUserValue.user?.fullName || 'User';
		this.clientId = this.userService.currentUserValue.user.clientId;
		const typingMsg: Message = {
			role: 'assistant',
			content: 'Hi there! How can I help you?',
			displayContent: '',
			safeContent: this.sanitizeHtml('Hi there! How can I help you?'),
			safeDisplayContent: '',
			typing: true,
			timestamp: new Date(),
		};
		this.messages.push(typingMsg);
		this.simulateTypingEffect(typingMsg);
	}

	async ngOnInit() {
		// A failed flag check must not break the chat; it just leaves the optional feature hidden.
		const [aiAgentsEnabled, claireEnabled] = await Promise.all([
			firstValueFrom(this.featureFlagService.isFeatureEnabledForClient(this.clientId, FEATURE_FLAGS.AI_AGENTS)).catch(
				() => false
			),
			firstValueFrom(
				this.featureFlagService.isFeatureEnabledForClient(this.clientId, FEATURE_FLAGS.PORTAL_AGENT)
			).catch(() => false),
		]);
		this.aiAgentsEnabled = aiAgentsEnabled;
		this.claireEnabled = claireEnabled;
	}

	get charCount(): string {
		return `${this.userInput.length} / ${this.charLimit}`;
	}

	scrollToBottom(): void {
		setTimeout(() => {
			if (this.chatScrollRef) {
				this.chatScrollRef.nativeElement.scrollTop = this.chatScrollRef.nativeElement.scrollHeight;
			}
		}, 100);
	}

	private buildReplyPayloadNonAgent() {
		return {
			History: this.messages.map((msg) => ({
				Role: msg.role,
				Content: msg.content,
			})),
		};
	}

	private buildReplyPayloadAgent(input: string) {
		return {
			userQuestion: input,
			threadId: this.threadId,
		};
	}

	sendMessage() {
		const input = this.userInput.trim();
		if (!input) return;

		const userMessage: Message = {
			role: 'user',
			content: input,
			timestamp: new Date(),
		};

		this.messages.push(userMessage);
		this.userInput = '';
		this.isLoading = true;
		this.scrollToBottom();

		const request$ = this.aiAgentsEnabled
			? this.aiAssistantService.getAgentReply(this.clientId, this.buildReplyPayloadAgent(input))
			: this.aiAssistantService.getReply(this.clientId, this.buildReplyPayloadNonAgent());

		request$.subscribe({
			next: (response) => {
				if (this.aiAgentsEnabled && response.threadId) {
					this.threadId = response.threadId;
				}

				const fullText = response.answer ?? response.content ?? '';

				const typingMsg: Message = {
					role: 'assistant',
					content: fullText,
					displayContent: '',
					safeContent: this.sanitizeHtml(fullText),
					safeDisplayContent: '',
					typing: true,
					timestamp: new Date(),
				};

				this.messages.push(typingMsg);
				this.simulateTypingEffect(typingMsg);
				this.isLoading = false;
			},
			error: (err) => {
				console.error('API error:', err);
				this.isLoading = false;
			},
		});
	}

	simulateTypingEffect(msg: Message) {
		let index = 0;
		const interval = setInterval(() => {
			if (index < (msg.content?.length || 0)) {
				msg.displayContent += msg.content ? msg.content[index] : '';
				msg.safeDisplayContent = this.sanitizeHtml(msg.displayContent);
				index++;
				this.typingSound.play().catch(() => {
					// Sound may not play if the browser has restrictions
				});
				this.scrollToBottom();
			} else {
				msg.typing = false;
				clearInterval(interval);
				this.scrollToBottom();
			}
		}, 15); // typing speed (ms per character)
	}

	private sanitizeHtml(value: string): string {
		return this.sanitizationService.sanitizeHtml(value);
	}

	onFollowUpClick(question: string) {
		this.userInput = question;
		this.sendMessage();
	}
}
